import type { Route, Step, ChainType, TokenMetaOutput, ChainMetaOutput } from "~/api/types";
import type { SwapSide } from "~/components/SwapCard/SwapCard";
import type { StepState, StepStatus } from "~/hooks/useExecuteSteps";
import { useChains } from "~/hooks/useChains";
import { formatAmount, truncateAddress } from "~/lib/format";
import { getExplorerTxUrl } from "~/lib/explorer";

// ─── Helpers ───

function getStepDescription(step: Step): string {
  switch (step.type) {
    case "APPROVAL":
      return `Approve ${step.token.symbol ?? "token"} spending`;
    case "PERMIT":
      return `Permit ${step.token.symbol ?? "token"}`;
    case "SWAP":
      return `Swap ${step.from.symbol ?? "?"} to ${step.to.symbol ?? "?"}`;
    case "BRIDGE": {
      const verb = step.fromChainId === step.toChainId ? "Swap" : "Bridge";
      return `${verb} ${step.from.symbol ?? "?"} to ${step.to.symbol ?? "?"}`;
    }
    case "ORDER_SUBMISSION":
      return `Sign ${step.from.symbol ?? "?"} order`;
    case "CLAIM":
      return `Claim ${step.token.symbol ?? "tokens"}`;
    case "WRAP":
      return `Wrap ${step.from.symbol ?? "?"}`;
    case "UNWRAP":
      return `Unwrap ${step.from.symbol ?? "?"}`;
  }
}

function getStatusText(status: StepStatus, error?: string): string | null {
  switch (status) {
    case "idle":
      return null;
    case "pending":
      return "Waiting...";
    case "signing":
      return "Confirm in wallet";
    case "submitting":
      return "Submitting...";
    case "polling":
      return "Confirming on-chain...";
    case "completed":
      return "Confirmed";
    case "failed":
      return error ?? "Failed";
  }
}

function getStepChainId(step: Step): string {
  if (step.type === "BRIDGE") return step.fromChainId;
  return step.chainId;
}

// ─── Sub-components ───

function Spinner({ size = 14 }: { size?: number }) {
  return (
    <svg
      className="animate-spin"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        className="opacity-25"
      />
      <path
        d="M4 12a8 8 0 018-8"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TokenIcon({
  token,
  chain,
  size = 32,
}: {
  token: TokenMetaOutput | null;
  chain: ChainMetaOutput | null;
  size?: number;
}) {
  const badgeSize = Math.round(size * 0.5);
  const badgeRadius = size > 24 ? 4 : 2.5;

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      {token?.logoUri ? (
        <img
          src={token.logoUri}
          alt={token.symbol}
          className="rounded-full"
          style={{ width: size, height: size }}
        />
      ) : (
        <div
          className="flex items-center justify-center rounded-full bg-[var(--color-bg-tertiary)] text-xs font-bold text-[var(--color-text-secondary)]"
          style={{ width: size, height: size }}
        >
          {token?.symbol?.slice(0, 2) ?? "?"}
        </div>
      )}
      {chain?.logoUri && (
        <div
          className="absolute -right-0.5 -bottom-0.5 overflow-hidden border border-[var(--color-bg-page)] bg-[var(--color-bg-page)]"
          style={{
            width: badgeSize,
            height: badgeSize,
            borderRadius: badgeRadius,
          }}
        >
          <img
            src={chain.logoUri}
            alt={chain.name}
            className="h-full w-full"
            style={{ borderRadius: badgeRadius }}
          />
        </div>
      )}
    </div>
  );
}

function TokenCard({
  token,
  chain,
  amount,
}: {
  token: TokenMetaOutput | null;
  chain: ChainMetaOutput | null;
  amount: string;
}) {
  return (
    <div className="flex flex-1 items-center gap-2.5 rounded-xl bg-[var(--color-bg-card-section)] p-3">
      <TokenIcon token={token} chain={chain} size={32} />
      <div className="flex min-w-0 flex-col">
        <span className="text-xs text-[var(--color-text-secondary)]">
          {chain?.name ?? ""}
        </span>
        <span className="truncate text-sm font-bold text-[var(--color-text-primary)]">
          {amount} {token?.symbol ?? ""}
        </span>
      </div>
    </div>
  );
}

function StepTypeIcon({ step }: { step: Step }) {
  const isCrossChainBridge =
    step.type === "BRIDGE" && step.fromChainId !== step.toChainId;

  switch (step.type) {
    case "APPROVAL":
    case "PERMIT":
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 2L4 7v5c0 5.25 3.4 10.15 8 11.25C16.6 22.15 20 17.25 20 12V7l-8-5z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "BRIDGE":
      if (!isCrossChainBridge) break; // same-chain → use default swap icon
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path
            d="M5 12h14M12 5l7 7-7 7"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    default:
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path
            d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
  }
}

// ─── Main Component ───

export function TransactionStatus({
  route,
  from,
  to,
  stepStates,
  currentIndex,
  onRetry,
  onReset,
}: {
  route: Route;
  from: SwapSide;
  to: SwapSide;
  stepStates: StepState[];
  currentIndex: number;
  onRetry: () => void;
  onReset: () => void;
}) {
  const { getChain } = useChains();
  const allCompleted = stepStates.every((s) => s.status === "completed");
  const hasFailed = stepStates.some((s) => s.status === "failed");

  const fromAmount = from.token
    ? formatAmount(route.summary.fromAmount, from.token.decimals)
    : "";
  const toAmount = to.token
    ? formatAmount(route.summary.toAmountEstimated, to.token.decimals)
    : "";

  // Explorer link for the last completed step
  const lastDone = [...stepStates]
    .reverse()
    .find((s) => s.status === "completed" && s.txHash);
  let explorerUrl: string | null = null;
  if (lastDone?.txHash) {
    const chainId = getStepChainId(lastDone.step);
    const chain = getChain(chainId);
    const ct: ChainType = chain?.chainType ?? "evm";
    explorerUrl = chain
      ? getExplorerTxUrl(ct, chain.explorerUrl, lastDone.txHash)
      : null;
  }

  const provider = route.summary.provider;

  // ─── Completed ───
  if (allCompleted) {
    return (
      <div className="flex flex-col gap-4">
        {/* Success header */}
        <div className="flex flex-col items-center gap-3 py-4">
          <div className="flex items-center">
            {provider.logoUrl && (
              <img
                src={provider.logoUrl}
                alt={provider.displayName}
                className="h-10 w-10 rounded-xl"
              />
            )}
            <div className="-ml-2 flex h-10 w-10 items-center justify-center rounded-full border-2 border-[var(--color-bg-page)] bg-[var(--color-success-step)]">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12l5 5L19 7"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
          <p className="text-base font-bold text-[var(--color-text-primary)]">
            Transaction Completed
          </p>
        </div>

        {/* Sent / Received */}
        <div className="flex flex-col gap-4 rounded-xl border border-[var(--color-border)] p-4">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-[var(--color-text-secondary)]">
              Sent
            </span>
            <div className="flex items-center gap-2">
              <TokenIcon token={from.token} chain={from.chain} size={20} />
              <span className="font-bold text-[var(--color-text-primary)]">
                {fromAmount} {from.token?.symbol}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-sm text-[var(--color-text-secondary)]">
              Received
            </span>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TokenIcon token={to.token} chain={to.chain} size={20} />
                <span className="font-bold text-[var(--color-text-primary)]">
                  {toAmount} {to.token?.symbol}
                </span>
              </div>
              {explorerUrl && lastDone?.txHash && (
                <a
                  href={explorerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[var(--color-link)] hover:underline"
                >
                  {truncateAddress(lastDone.txHash)}
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          {explorerUrl && (
            <a
              href={explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 rounded-xl border border-[var(--color-border)] px-5 py-3 text-center text-sm font-bold text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-tertiary)]"
            >
              View Details
            </a>
          )}
          <button
            onClick={onReset}
            className="w-full rounded-xl bg-[var(--color-accent)] py-3 text-sm font-bold text-white transition-colors hover:bg-[var(--color-accent-hover)]"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // ─── In Progress / Failed ───
  return (
    <div className="flex flex-col gap-4">
      <p className="text-base font-semibold text-[var(--color-text-primary)]">
        Transaction Details
      </p>

      {/* From → To cards */}
      <div className="flex items-center gap-2">
        <TokenCard token={from.token} chain={from.chain} amount={fromAmount} />
        <svg
          width="16"
          height="16"
          viewBox="0 0 448 512"
          fill="currentColor"
          className="shrink-0 text-[var(--color-text-tertiary)]"
        >
          <path d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l306.7 0L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z" />
        </svg>
        <TokenCard token={to.token} chain={to.chain} amount={toAmount} />
      </div>

      {/* Step list */}
      <div className="flex flex-col overflow-hidden rounded-xl border border-[var(--color-border)]">
        {route.steps.map((step, i) => {
          const state = stepStates[i];
          const status = state?.status ?? "idle";
          const isActive = i === currentIndex;
          const statusText = getStatusText(status, state?.error);
          const isInProgress =
            status === "signing" ||
            status === "submitting" ||
            status === "polling";

          return (
            <div
              key={step.stepId}
              className={`flex items-center gap-3 px-4 py-3 ${
                i > 0 ? "border-t border-[var(--color-border)]" : ""
              }`}
            >
              {/* Indicator */}
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors ${
                  status === "completed"
                    ? "bg-[var(--color-success-step)] text-white"
                    : status === "failed"
                      ? "bg-[var(--color-danger)] text-white"
                      : isActive && isInProgress
                        ? "bg-[var(--color-accent)] text-white"
                        : "bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]"
                }`}
              >
                {status === "completed" ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12l5 5L19 7"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : status === "failed" ? (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M4 4l8 8M12 4l-8 8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : isActive && isInProgress ? (
                  <Spinner size={16} />
                ) : (
                  <StepTypeIcon step={step} />
                )}
              </div>

              {/* Description + status */}
              <div className="flex min-w-0 flex-1 flex-col">
                <span
                  className={`text-sm font-medium ${
                    isActive
                      ? "text-[var(--color-text-primary)]"
                      : "text-[var(--color-text-secondary)]"
                  }`}
                >
                  {getStepDescription(step)}
                </span>
                {statusText && (
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`text-xs ${
                        status === "completed"
                          ? "text-[var(--color-success-step)]"
                          : status === "failed"
                            ? "text-[var(--color-danger)]"
                            : "text-[var(--color-accent)]"
                      }`}
                    >
                      {statusText}
                    </span>
                    {isInProgress && (
                      <Spinner size={12} />
                    )}
                  </div>
                )}
              </div>

              {/* Tx link */}
              {state?.txHash
                ? (() => {
                    const chainId = getStepChainId(step);
                    const chain = getChain(chainId);
                    const ct: ChainType = chain?.chainType ?? "evm";
                    const txUrl = chain
                      ? getExplorerTxUrl(ct, chain.explorerUrl, state.txHash)
                      : null;
                    return txUrl ? (
                      <a
                        href={txUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 text-xs text-[var(--color-link)] hover:underline"
                      >
                        {truncateAddress(state.txHash)}
                      </a>
                    ) : null;
                  })()
                : null}
            </div>
          );
        })}
      </div>

      {/* Failed actions */}
      {hasFailed && (
        <div className="flex gap-2">
          <button
            onClick={onRetry}
            className="flex-1 rounded-xl bg-[var(--color-accent)] py-3 text-sm font-bold text-white transition-colors hover:bg-[var(--color-accent-hover)]"
          >
            Retry
          </button>
          <button
            onClick={onReset}
            className="rounded-xl border border-[var(--color-border)] px-5 py-3 text-sm text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-tertiary)]"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
